import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, formValueSelector } from 'redux-form';
import { RenderField, RenderSelectField } from './index';
import * as Validations from './commonValidations';
import * as AddressData from './CountryCodes';
// import featureFlags from '../../../util/featureFlags';

const AddressFields = props => {
  const { stateValues, nonUs, optional, minimal, disabled, addressMessage } = props;

  const zipValidation = () => {
    if (optional) {
      return nonUs() ? Validations.MAX_LENGTH_15 : Validations.ZIP;
    }
    return nonUs()
      ? [Validations.REQUIRED, Validations.MAX_LENGTH_15]
      : [Validations.REQUIRED, Validations.ZIP];
  };

  return (
    <div>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field
            name="address_1"
            label="Street Address"
            type="text"
            id="address-street-1"
            component={RenderField}
            required={!optional}
            maxChars={100}
            validate={
              optional
                ? Validations.MAX_LENGTH_100
                : [Validations.MAX_LENGTH_100, Validations.REQUIRED]
            }
            disabled={disabled}
            helpMessage={addressMessage}
            autoComplete="address-line1"
          />
        </div>
        <div className="col-xs-12 col-sm-6">
          <Field
            name="address_2"
            label="Apt / Floor"
            type="text"
            id="address-street-2"
            maxChars={100}
            validate={Validations.MAX_LENGTH_100}
            component={RenderField}
            disabled={disabled}
            autoComplete="address-line2"
          />
        </div>
      </div>
      {!minimal && (
        <div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <Field
                name="city"
                label="City"
                type="text"
                id="address-city"
                component={RenderField}
                required={!optional}
                maxChars={35}
                validate={Validations.MAX_LENGTH_35}
                disabled={disabled}
                autoComplete="address-level2"
              />
            </div>
            <div className="col-xs-12 col-sm-6">
              {stateValues() && (
                <Field
                  name="state_province"
                  label={nonUs() ? 'Province' : 'State'}
                  options={stateValues()}
                  id="address-state-province"
                  component={RenderSelectField}
                  required={!optional}
                  validate={Validations.MAX_LENGTH_35}
                  disabled={disabled}
                  autoComplete="address-level1"
                />
              )}
              {!stateValues() && (
                <Field
                  name="state_province"
                  label="State / Region"
                  type="text"
                  id="address-state-province"
                  component={RenderField}
                  required={!optional}
                  validate={Validations.MAX_LENGTH_35}
                  disabled={disabled}
                  autoComplete="address-level1"
                />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <Field
                name="postal_code"
                label={nonUs() ? 'Postal Code' : 'Zip Code'}
                type="text"
                id="address-postal-code"
                component={RenderField}
                required={!optional}
                validate={zipValidation()}
                disabled={disabled}
                autoComplete="postal-code"
              />
            </div>
            <div className="col-xs-12 col-sm-6">
              <Field
                name="contact_phone"
                label="Phone Number"
                type="tel"
                id="address-phone-number"
                component={RenderField}
                required={!optional}
                maxChars={25}
                validate={
                  optional
                    ? Validations.MAX_LENGTH_25
                    : [Validations.REQUIRED, Validations.MAX_LENGTH_25]
                }
                disabled={disabled}
                autoComplete="tel"
              />
            </div>
          </div>
        </div>
      )}
      {minimal && (
        <div>
          <div className="row">
            <div className="col-sm-6 col-xs-12">
              <Field
                name="city"
                label="City"
                type="text"
                id="address-city"
                component={RenderField}
                required={!optional}
                maxChars={35}
                validate={
                  optional
                    ? Validations.MAX_LENGTH_35
                    : [Validations.MAX_LENGTH_35, Validations.REQUIRED]
                }
                disabled={disabled}
                autoComplete="address-level2"
              />
            </div>
            <div className="col-sm-3 col-xs-6">
              {stateValues() && (
                <Field
                  name="state_province"
                  label={nonUs() ? 'Province' : 'State'}
                  options={stateValues()}
                  id="address-state-province"
                  component={RenderSelectField}
                  required={!optional}
                  validate={optional ? undefined : Validations.REQUIRED}
                  disabled={disabled}
                  autoComplete="address-level1"
                />
              )}
              {!stateValues() && (
                <Field
                  name="state_province"
                  label="State / Region"
                  type="text"
                  id="address-state-province"
                  component={RenderField}
                  required={!optional}
                  validate={Validations.MAX_LENGTH_35}
                  disabled={disabled}
                  autoComplete="address-level1"
                />
              )}
            </div>
            <div className="col-sm-3 col-xs-6">
              <Field
                name="postal_code"
                label={nonUs() ? 'Postal Code' : 'Zip Code'}
                type="text"
                id="address-postal-code"
                component={RenderField}
                required={!optional}
                validate={zipValidation()}
                disabled={disabled}
                autoComplete="postal-code"
              />
            </div>
          </div>
        </div>
      )}
      <Field
        name="country_code"
        label="Country"
        id="address-country-code"
        options={AddressData.Countries}
        component={RenderSelectField}
        required={!optional}
        validate={optional ? undefined : Validations.REQUIRED}
        disabled={disabled}
        hideEmpty
        autoComplete="country"
      />
    </div>
  );
};

AddressFields.propTypes = {
  stateValues: PropTypes.func,
  nonUs: PropTypes.func,
  optional: PropTypes.bool,
  minimal: PropTypes.bool,
  disabled: PropTypes.bool,
  addressMessage: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => ({
  stateValues: () => {
    const selector = formValueSelector(ownProps.selector);
    const prefix = ownProps.prefix ? `${ownProps.prefix}.` : '';
    switch (selector(state, `${prefix}country_code`)) {
      case 'US':
        return AddressData.UnitedStates;
      case 'CA':
        return AddressData.Canada;
      case undefined:
        return AddressData.UnitedStates;
      default:
        return undefined;
    }
  },
  nonUs: () => {
    const selector = formValueSelector(ownProps.selector);
    const prefix = ownProps.prefix ? `${ownProps.prefix}.` : '';
    return (
      selector(state, `${prefix}country_code`) !== 'US' &&
      selector(state, `${prefix}country_code`) !== undefined
    );
  },
});

const AddressFieldsContainer = connect(mapStateToProps)(AddressFields);

export default AddressFieldsContainer;



// WEBPACK FOOTER //
// ./src/components/common/form/AddressFields.jsx